import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import api from '../../utils/api';
import CheckoutForm from './CheckoutForm';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const Pricing = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState({});
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [showCheckout, setShowCheckout] = useState(false);
  const [userSubscription, setUserSubscription] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Check if user is authenticated
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    setIsAuthenticated(!!token);
    
    if (token) {
      fetchPlans();
      fetchUserSubscription();
    } else {
      // If not authenticated, show plans but don't fetch subscription
      fetchPlans();
      setLoading(false);
    }
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await api.get('/api/subscribe/plans');
      setPlans(response.data.plans);
    } catch (error) {
      console.error('Error fetching plans:', error);
      // Fallback to hardcoded plans if API fails
      setPlans({
        free: {
          name: 'CartResQ Lite',
          price: 0,
          features: [
            '1 connected store (WooCommerce or Shopify)',
            'Detect and view abandoned carts',
            '1 automated email recovery workflow',
            'Send up to 300 recovery emails/month',
            'Basic cart analytics (total carts, recovery rate)',
            'Manual coupon creation',
            'Responsive dashboard'
          ]
        },
        starter: {
          name: 'CartResQ Starter',
          price: 19,
          features: [
            'Everything in Free, plus:',
            'Up to 2 connected stores',
            'Up to 4,000 recovery emails/month',
            'Unlimited automated email workflows',
            'Pre-built cart recovery email templates',
            'Push Notification sending',
            'Custom discount coupons with auto-sync',
            'Advanced cart and coupon analytics',
            'Scheduled email campaigns',
            'Customer segmentation',
            'Real-time notifications',
            'Priority email/chat support'
          ]
        },
        growth: {
          name: 'CartResQ Growth',
          price: 49,
          features: [
            'Everything in Starter, plus:',
            'Up to 2 WooCommerce + 2 Shopify stores',
            'Up to 5,000 recovery emails/month',
            'Automated Push Notification recovery messages',
            'Full communication history tracking',
            'Advanced segmentation: behavior + cart value',
            'Multi-store dashboard with cross-store comparison',
            'Real-time analytics (conversion rates, top products, CLV)',
            'ROI tracking dashboard',
            'Notification preferences management',
            'Export analytics/chart data',
            'Premium support (email, chat, onboarding help)'
          ]
        },
        test: {
          name: 'CartResQ Test Plan',
          price: 1,
          features: [
            'Test plan for subscription verification',
            'Same features as Starter plan',
            'Use Stripe test mode for testing',
            'Perfect for testing subscription flow',
            'Up to 2 connected stores',
            'Up to 4,000 recovery emails/month',
            'Unlimited automated email workflows',
            'Push Notification sending',
            'Custom discount coupons',
            'Advanced analytics',
            'Priority support'
          ]
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchUserSubscription = async () => {
    try {
      const response = await api.get('/api/subscribe/status');
      setUserSubscription(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const handlePlanSelect = async (planKey) => {
    // If user is not authenticated, redirect to login first
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (planKey === 'free') {
      // For free plan, create a free subscription first
      console.log('Creating free subscription...');
      try {
        const response = await api.post('/api/subscribe/create-free-subscription');
        console.log('Free subscription response:', response.data);
        if (response.data.success) {
          console.log('Free subscription created successfully, navigating to store setup');
          // After creating free subscription, navigate to store setup
          navigate('/store-setup');
        } else {
          console.error('Failed to create free subscription:', response.data.message);
          // Still navigate to store setup even if API call fails
          navigate('/store-setup');
        }
      } catch (error) {
        console.error('Error creating free subscription:', error);
        console.error('Error details:', error.response?.data);
        // Still navigate to store setup even if API call fails
        navigate('/store-setup');
      }
      return;
    }
    setSelectedPlan(planKey);
    setShowCheckout(true);
  };

  const handleCheckoutSuccess = () => {
    setShowCheckout(false);
    fetchUserSubscription();
  };

  const handleCheckoutCancel = () => {
    setShowCheckout(false);
    setSelectedPlan(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (showCheckout && selectedPlan) {
    return (
      <Elements stripe={stripePromise}>
        <CheckoutForm
          plan={plans[selectedPlan]}
          planKey={selectedPlan}
          billingCycle={billingCycle}
          onSuccess={handleCheckoutSuccess}
          onCancel={handleCheckoutCancel}
        />
      </Elements>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Simple, Transparent
            <span className="text-gradient">Pricing</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Choose the plan that fits your business size and watch your revenue grow
          </p>
        </div>

        {/* Billing Cycle Toggle */}
        <div className="flex justify-center mb-8">
          <div className="bg-white rounded-lg p-1 shadow-sm">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                billingCycle === 'monthly'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                billingCycle === 'yearly'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Yearly
              <span className="ml-1 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                Save 20%
              </span>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {Object.entries(plans)
            .filter(([planKey]) => planKey !== 'test')
            .map(([planKey, plan], index) => {
            const isPopular = planKey === 'starter';
            const isFree = planKey === 'free';
            const isTest = planKey === 'test';
            const yearlyPrice = billingCycle === 'yearly' ? Math.round(plan.price * 0.8) : plan.price;
            
            return (
              <div 
                key={planKey}
                className={`rounded-2xl p-8 card-hover relative ${
                  isPopular 
                    ? 'bg-gradient-to-b from-indigo-500 to-purple-600 text-white' 
                    : isTest
                    ? 'bg-gradient-to-b from-orange-400 to-red-500 text-white border-2 border-orange-300'
                    : 'bg-gray-50'
                }`}
              >
                {isPopular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-yellow-400 text-gray-900 px-4 py-1 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}
                {isTest && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-yellow-400 text-gray-900 px-4 py-1 rounded-full text-sm font-semibold animate-pulse">
                      🧪 TEST PLAN
                    </span>
                  </div>
                )}
                
                <div className="text-center">
                  <h3 className={`text-xl font-semibold mb-2 ${isPopular ? '' : 'text-gray-900'}`}>
                    {plan.name}
                  </h3>
                  <p className={`mb-6 ${isPopular ? 'text-indigo-100' : isTest ? 'text-orange-100' : 'text-gray-600'}`}>
                    {isFree 
                      ? 'Perfect for getting started'
                      : isTest
                      ? 'For testing subscription flow'
                      : planKey === 'starter'
                      ? 'Perfect for small businesses'
                      : 'For growing businesses'
                    }
                  </p>
                  <div className="mb-6">
                    <span className={`text-4xl font-bold ${isPopular || isTest ? '' : 'text-gray-900'}`}>
                      ${yearlyPrice}
                    </span>
                    <span className={isPopular ? 'text-indigo-200' : isTest ? 'text-orange-200' : 'text-gray-500'}>/month</span>
                    {billingCycle === 'yearly' && plan.price > 0 && !isTest && (
                      <div className={`text-sm ${isPopular ? 'text-indigo-200' : 'text-gray-500'} line-through`}>
                        ${plan.price}/month
                      </div>
                    )}
                    {isTest && (
                      <div className="text-xs text-orange-200 mt-1">
                        Test mode only
                      </div>
                    )}
                  </div>
                </div>
                
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center">
                      <svg className={`w-5 h-5 mr-3 ${isPopular ? 'text-green-300' : 'text-green-500'}`} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className={isPopular ? '' : 'text-gray-700'}>{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <button
                  onClick={() => handlePlanSelect(planKey)}
                  className={`w-full py-3 rounded-full font-semibold transition-colors ${
                    isFree
                      ? 'bg-gray-900 hover:bg-gray-800 text-white'
                      : isTest
                      ? 'bg-white text-orange-600 hover:bg-gray-100'
                      : isPopular
                      ? 'bg-white text-indigo-600 hover:bg-gray-100'
                      : 'bg-gray-900 hover:bg-gray-800 text-white'
                  }`}
                >
                  {isFree ? 'Start Free Trial' : isTest ? '🧪 Test Subscription' : 'Start Free Trial'}
                </button>
              </div>
            );
          })}
        </div>

        {/* FAQ Section */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Frequently Asked Questions
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Can I change my plan anytime?
              </h3>
              <p className="text-gray-600">
                Yes, you can upgrade or downgrade your plan at any time. Changes will be prorated and reflected in your next billing cycle.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Is there a free trial?
              </h3>
              <p className="text-gray-600">
                Yes! We offer a free plan with basic features. You can also try our paid plans with a 14-day free trial.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                What payment methods do you accept?
              </h3>
              <p className="text-gray-600">
                We accept all major credit cards (Visa, MasterCard, American Express) and debit cards through our secure payment processor.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Can I cancel my subscription?
              </h3>
              <p className="text-gray-600">
                Yes, you can cancel your subscription at any time. You'll continue to have access until the end of your current billing period.
              </p>
            </div>
          </div>
        </div>

        {/* Custom Pricing & Enterprise */}
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl p-8 text-white text-center mb-12">
          <h2 className="text-3xl font-bold mb-4">Need Custom Pricing?</h2>
          <p className="text-xl text-indigo-100 mb-6 max-w-2xl mx-auto">
            For enterprise customers, custom integrations, or specialized requirements, 
            we offer tailored solutions to meet your specific needs.
          </p>
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white/10 rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-2">Enterprise Plans</h3>
              <p className="text-indigo-100 text-sm">Custom pricing for large-scale operations</p>
            </div>
            <div className="bg-white/10 rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-2">White-label Solutions</h3>
              <p className="text-indigo-100 text-sm">Branded solutions for agencies and resellers</p>
            </div>
            <div className="bg-white/10 rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-2">Custom Integrations</h3>
              <p className="text-indigo-100 text-sm">Tailored integrations for unique requirements</p>
            </div>
          </div>
          <a
            href="mailto:support@cartresq.com?subject=Custom Pricing Inquiry"
            className="inline-flex items-center px-8 py-4 bg-white text-indigo-600 rounded-full font-semibold text-lg hover:bg-gray-100 transition-all transform hover:scale-105 shadow-xl"
          >
            Contact Sales Team
          </a>
        </div>

        {/* Contact Support */}
        <div className="text-center mt-12">
          <p className="text-gray-600 mb-4">
            All plans include a 14-day free trial • No credit card required
          </p>
          <div className="flex justify-center space-x-6 text-sm text-gray-500 mb-6">
            <span>✓ Cancel anytime</span>
            <span>✓ 30-day money-back guarantee</span>
            <span>✓ Free migration assistance</span>
          </div>
          <p className="text-gray-600 mb-4">
            Need help choosing the right plan?
          </p>
          <a
            href="mailto:support@cartresq.com"
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-full text-indigo-600 bg-indigo-100 hover:bg-indigo-200 transition-colors"
          >
            Contact Support
          </a>
          <p className="text-sm text-gray-500 mt-4">
            Email: support@cartresq.com
          </p>
        </div>
      </div>
    </div>
  );
};

export default Pricing; 